import { useEffect, useMemo, useState } from 'react';
import { Caption1, Input, MessageBar, MessageBarBody, Spinner, Text, tokens } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import type { AccessMatrixDto, RoleAccessDto, RoleUserDto } from '../api/types';

const PURPLE = '91, 95, 199';
const GREEN = '29, 158, 117';
const heat = (rgb: string, ratio: number) => `rgba(${rgb}, ${(0.16 + 0.84 * Math.min(1, ratio)).toFixed(3)})`;

export function IamHeatmapTab() {
  const api = useApi();
  const [matrix, setMatrix] = useState<AccessMatrixDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApps, setShowApps] = useState(true);
  const [showEApps, setShowEApps] = useState(true);
  const [selected, setSelected] = useState<RoleAccessDto | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .getRoleAccessMatrix()
      .then(setMatrix)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [api]);

  const { appCell, eappCell, appMax, eappMax } = useMemo(() => {
    const appCell = new Map<string, Map<string, number>>();
    const eappCell = new Map<string, Map<string, number>>();
    let appMax = 1;
    let eappMax = 1;
    for (const r of matrix?.roles ?? []) {
      const am = new Map<string, number>();
      for (const a of r.apps) {
        am.set(a.app, a.count);
        if (a.count > appMax) appMax = a.count;
      }
      appCell.set(r.roleId, am);
      const em = new Map<string, number>();
      for (const a of r.enterpriseApps) {
        em.set(a.app, a.count);
        if (a.count > eappMax) eappMax = a.count;
      }
      eappCell.set(r.roleId, em);
    }
    return { appCell, eappCell, appMax, eappMax };
  }, [matrix]);

  if (loading) return <Spinner label="Lade Heatmap…" />;
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error}</MessageBarBody>
      </MessageBar>
    );
  }
  if (!matrix || matrix.roles.length === 0) {
    return <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Noch keine Rollen mit Berechtigungen.</Caption1>;
  }

  const apps = showApps ? matrix.apps : [];
  const eapps = showEApps ? matrix.enterpriseApps : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <Text weight="semibold">Zugriffs-Heatmap</Text>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Rolle × App – Intensität nach Anzahl</Caption1>
        <div className="iam-pillbar" style={{ marginLeft: 'auto', marginBottom: 0 }}>
          <SectionPill active={showApps} rgb={PURPLE} label="Apps" onClick={() => setShowApps((v) => !v)} />
          <SectionPill active={showEApps} rgb={GREEN} label="Enterprise Apps" onClick={() => setShowEApps((v) => !v)} />
        </div>
      </div>

      <div className="iam-editor-grid">
        <div className="iam-card" style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <table className="iam-heat-table">
            <thead>
              <tr>
                <th className="iam-heat-corner">Rolle</th>
                <th className="iam-heat-kpi">User</th>
                <th className="iam-heat-kpi">Gruppen</th>
                {apps.map((app) => (
                  <th key={`a:${app}`} className="iam-heat-diag" title={app}><span>{app}</span></th>
                ))}
                {eapps.map((app) => (
                  <th key={`e:${app}`} className="iam-heat-diag" title={app}><span>{app}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.roles.map((r) => {
                const am = appCell.get(r.roleId) ?? new Map();
                const em = eappCell.get(r.roleId) ?? new Map();
                const sel = selected?.roleId === r.roleId;
                return (
                  <tr key={r.roleId} className={sel ? 'sel' : undefined} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                    <td className="iam-heat-rowhead pii" title={r.roleName}>{r.roleName}</td>
                    <td className="iam-heat-kpi">{r.userCount}</td>
                    <td className="iam-heat-kpi">{r.groupCount}</td>
                    {apps.map((app) => {
                      const c = am.get(app) ?? 0;
                      return <HeatCell key={`a:${app}`} count={c} ratio={c / appMax} rgb={PURPLE} label={r.roleName} app={app} />;
                    })}
                    {eapps.map((app) => {
                      const c = em.get(app) ?? 0;
                      return <HeatCell key={`e:${app}`} count={c} ratio={c / eappMax} rgb={GREEN} label={r.roleName} app={app} />;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected ? (
          <RoleDetail role={selected} onClose={() => setSelected(null)} />
        ) : (
          <div className="iam-card" style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Klicke eine Rolle, um Details und zugewiesene Anwender zu sehen.</Caption1>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionPill({ active, rgb, label, onClick }: { active: boolean; rgb: string; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`iam-pillbtn${active ? ' active' : ''}`} onClick={onClick} aria-pressed={active}>
      <span className="iam-dot" style={{ background: active ? `rgb(${rgb})` : 'transparent', border: `2px solid rgb(${rgb})` }} />
      {label}
    </button>
  );
}

function HeatCell({ count, ratio, rgb, label, app }: { count: number; ratio: number; rgb: string; label: string; app: string }) {
  return (
    <td
      title={count > 0 ? `${label} · ${app}: ${count}` : undefined}
      style={{ background: count > 0 ? heat(rgb, ratio) : 'transparent', color: ratio > 0.55 ? '#fff' : tokens.colorNeutralForeground1 }}
    >
      {count > 0 ? count : ''}
    </td>
  );
}

type UserSort = { col: 'name' | 'dept' | 'type'; dir: 'asc' | 'desc' };

function RoleDetail({ role, onClose }: { role: RoleAccessDto; onClose: () => void }) {
  const api = useApi();
  const [users, setUsers] = useState<RoleUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<UserSort>({ col: 'name', dir: 'asc' });

  useEffect(() => {
    setLoading(true);
    setSearch('');
    api
      .getRoleAssignedUsers(role.roleId)
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [api, role.roleId]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? users.filter((u) => u.displayName.toLowerCase().includes(q) || (u.department ?? '').toLowerCase().includes(q))
      : users;
    const val = (u: RoleUserDto) =>
      sort.col === 'name' ? u.displayName.toLowerCase() : sort.col === 'dept' ? (u.department ?? '').toLowerCase() : u.type;
    return [...filtered].sort((a, b) => {
      const c = String(val(a)).localeCompare(String(val(b)));
      return sort.dir === 'asc' ? c : -c;
    });
  }, [users, search, sort]);

  function head(label: string, col: UserSort['col']) {
    const active = sort.col === col;
    return (
      <th onClick={() => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }))}>
        {label}
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );
  }

  return (
    <div className="iam-card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '74vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text weight="semibold" className="pii" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {role.roleName}
        </Text>
        <button type="button" className="iam-pillbtn" style={{ padding: '2px 10px' }} onClick={onClose}>Schließen</button>
      </div>

      <div className="iam-kpis" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 12, marginBottom: 12 }}>
        <Mini value={role.userCount} label="User" />
        <Mini value={role.groupCount} label="Gruppen" />
        <Mini value={role.appCount} label="Apps" />
        <Mini value={role.eAppCount} label="Enterprise Apps" />
      </div>

      <Caption1 style={{ color: tokens.colorNeutralForeground3, marginBottom: 6 }}>Zugewiesene Anwender</Caption1>
      <Input
        contentBefore={<SearchRegular />}
        placeholder="Name oder Abteilung…"
        value={search}
        onChange={(_, d) => setSearch(d.value)}
        style={{ marginBottom: 8 }}
      />

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Spinner size="tiny" label="Lade Anwender…" />
        ) : rows.length === 0 ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{users.length === 0 ? 'Keine direkten Zuweisungen.' : 'Keine Treffer.'}</Caption1>
        ) : (
          <table className="iam-table">
            <thead>
              <tr>
                {head('Name', 'name')}
                {head('Abteilung', 'dept')}
                {head('Typ', 'type')}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={`${u.type}:${u.id}`}>
                  <td className="pii">{u.displayName}</td>
                  <td className="pii">{u.department ?? <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>}</td>
                  <td>{u.type === 'Group' ? 'Gruppe' : 'User'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Mini({ value, label }: { value: number; label: string }) {
  return (
    <div className="iam-kpi" style={{ padding: '10px 12px' }}>
      <div className="iam-kpi-val" style={{ fontSize: 22 }}>{value}</div>
      <div className="iam-kpi-lbl">{label}</div>
    </div>
  );
}
